import { Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { EXAM_SESSION_STATUS_LABELS } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Segmented } from '../../../components/Segmented';
import { SkeletonLines } from '../../../components/Skeleton';
import { EXAM_SESSION_TONES } from '../../../components/status-tones';
import { ToneChip } from '../../../components/Tone';
import { EmptyState, ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { EMPTY, formatDateTime } from '../../../lib/format';
import { useCanEdit, useCanView } from '../../../stores/session';
import { useExamSessions } from '../api/exam-sessions-api';
import { AssessmentsHeader } from '../components/AssessmentsHeader';
import { SessionEditor } from '../components/SessionEditor';
import styles from '../components/Assessments.module.css';


/**
 * ASM-04's list (F9.08). An invigilator sees only the sessions they supervise (§L23);
 * recruiters and the HR Lead see their jobs' sessions and can set new ones.
 */
export function ExamSessionsPage(): ReactNode {
  const canView = useCanView('exam_session');
  // An invigilator holds sessions in full but only their own, and no jobs to set one for.
  const canEditSessions = useCanEdit('exam_session');
  const canSeeJobs = useCanView('job_opening');
  const canCreate = canEditSessions && canSeeJobs;
  const [when, setWhen] = useState<'upcoming' | 'past'>('upcoming');
  const [isCreating, setIsCreating] = useState(false);
  const sessions = useExamSessions({ when });

  if (!canView) return <ForbiddenState resource="exam sessions" contact="Your admin" />;

  const newButton = canCreate ? (
    <Button variant="solid" size="sm" icon={Plus} onClick={() => setIsCreating(true)}>
      New session
    </Button>
  ) : undefined;

  return (
    <div className={styles.screen}>
      <AssessmentsHeader current="exam_sessions" />
      <Card
        title="Exam sessions"
        meta="On-site written exams. Each seat's mark lands on the job's Assessment tab"
        action={
          <div className={styles.itemActions}>
            <Segmented
              label="Which sessions"
              value={when}
              options={[
                { value: 'upcoming', label: 'Upcoming' },
                { value: 'past', label: 'Past' },
              ]}
              onChange={setWhen}
            />
            {newButton}
          </div>
        }
      >
        {sessions.isPending ? (
          <SkeletonLines count={4} />
        ) : sessions.error ? (
          <ServerErrorState onRetry={() => void sessions.refetch()} />
        ) : sessions.data.length === 0 ? (
          <EmptyState
            isInset
            title={when === 'upcoming' ? 'No exams coming up' : 'No past exams'}
            body={canCreate ? 'Set a session for a job whose test is sat on paper, then add the people in its Assessment stage.' : 'Sessions you supervise appear here.'}
            action={when === 'upcoming' ? newButton : undefined}
          />
        ) : (
          <ul className={styles.list}>
            {sessions.data.map((session) => (
              <li key={session.id} className={styles.item}>
                <div className={styles.itemHead}>
                  <Link to="/assessments/sessions/$sessionId" params={{ sessionId: session.id }} className={styles.itemName}>
                    {session.name}
                  </Link>
                  <ToneChip tone={EXAM_SESSION_TONES[session.status]}>{EXAM_SESSION_STATUS_LABELS[session.status]}</ToneChip>
                  <span className={styles.meta}>
                    {session.booked} of {session.capacity} booked
                  </span>
                </div>
                <p className={styles.route}>
                  {session.startsAt ? formatDateTime(session.startsAt) : 'No date yet'} · {session.venue ?? EMPTY} · {session.jobTitle} · invigilator {session.invigilatorName ?? EMPTY}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <SessionEditor isOpen={isCreating} onClose={() => setIsCreating(false)} session={null} />
    </div>
  );
}
